import { supabase } from '../lib/supabase';

export interface AnalyticsOverview {
  totalRevenue: number;
  totalStudents: number;
  totalCourses: number;
  averageRating: number;
  revenueGrowth: number;
  studentsGrowth: number;
  coursesGrowth: number;
  ratingGrowth: number;
  // Ajout des métriques alignées sur la page Courses
  potentialTotalRevenue: number;
  averageOfAverageRatings: number;
}

export interface CourseAnalytics {
  id: string;
  title: string;
  slug: string;
  enrollments: number;
  completions: number;
  revenue: number;
  averageRating: number;
  averageTimeSpent: number;
  completionRate: number;
  dropoffRate: number;
  viewsCount: number;
  lastWeekEnrollments: number;
  conversionRate: number;
}

export interface StudentEngagement {
  activeStudents: number;
  averageTimeSpent: number;
  completionRate: number;
  retentionRate: number;
  dailyActiveUsers: number;
  weeklyActiveUsers: number;
  monthlyActiveUsers: number;
}

export interface RevenueAnalytics {
  monthlyRevenue: MonthlyRevenue[];
  topSellingCourses: TopCourse[];
  revenueByCategory: CategoryRevenue[];
  averageOrderValue: number;
  totalTransactions: number;
}

export interface MonthlyRevenue {
  month: string;
  revenue: number;
  enrollments: number;
  growth: number;
}

export interface TopCourse {
  id: string;
  title: string;
  revenue: number;
  enrollments: number;
  averageRating: number;
}

export interface CategoryRevenue {
  categoryId: string;
  categoryName: string;
  revenue: number;
  percentage: number;
}

export interface ReviewAnalytics {
  totalReviews: number;
  averageRating: number;
  ratingDistribution: RatingDistribution[];
  recentReviews: Review[];
  sentimentAnalysis: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

export interface RatingDistribution {
  stars: number;
  count: number;
  percentage: number;
}

export interface Review {
  id: string;
  studentName: string;
  studentAvatar: string | null;
  courseName: string;
  rating: number;
  comment: string;
  date: string;
  isVerified: boolean;
}

export interface TimeSeriesData {
  date: string;
  enrollments: number;
  revenue: number;
  completions: number;
  activeStudents: number;
}

export interface GeographicData {
  country: string;
  students: number;
  revenue: number;
  percentage: number;
}

export interface StudentDetails {
  id: string;
  name: string;
  email: string;
  avatar_url: string | null;
  enrollment_date: string;
  last_activity: string;
  total_courses: number;
  completed_courses: number;
  total_progress: number;
  total_time_spent: number; // en minutes
  average_grade: number;
  status: 'active' | 'inactive' | 'completed';
  courses: {
    course_id: string;
    course_title: string;
    enrollment_date: string;
    progress: number;
    last_accessed: string;
    completion_date: string | null;
    grade: number | null;
    time_spent: number; // en minutes
  }[];
}

/**
 * Service pour les analytics des instructeurs
 */
export class InstructorAnalyticsService {

  /**
   * Récupère la vue d'ensemble des analytics
   */
  static async getAnalyticsOverview(
    tenantId: string,
    instructorId: string,
    timeRange: string = '30d'
  ): Promise<AnalyticsOverview> {
    try {
      const dateFilter = this.getDateFilter(timeRange);
      const previousDateFilter = this.getPreviousDateFilter(timeRange);

      // Récupération des cours de l'instructeur avec toutes les données nécessaires
      const { data: courses } = await supabase
        .from('elearn_courses')
        .select('id, price, enrollment_count, rating_average, rating_count')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      const courseIds = courses?.map(c => c.id) || [];

      // Revenus actuels
      const { data: currentRevenue } = await supabase
        .from('elearn_order_items')
        .select(`
          price_at_purchase,
          elearn_orders!inner(created_at, status)
        `)
        .eq('elearn_orders.tenant_id', tenantId)
        .in('course_id', courseIds)
        .eq('elearn_orders.status', 'completed')
        .gte('elearn_orders.created_at', dateFilter);

      // Revenus période précédente
      const { data: previousRevenue } = await supabase
        .from('elearn_order_items')
        .select(`
          price_at_purchase,
          elearn_orders!inner(created_at, status)
        `)
        .eq('elearn_orders.tenant_id', tenantId)
        .in('course_id', courseIds)
        .eq('elearn_orders.status', 'completed')
        .gte('elearn_orders.created_at', previousDateFilter.start)
        .lt('elearn_orders.created_at', previousDateFilter.end);

      // Étudiants actuels
      const { data: currentEnrollments } = await supabase
        .from('elearn_enrollments')
        .select('student_id')
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds)
        .gte('created_at', dateFilter);

      // Étudiants période précédente
      const { data: previousEnrollments } = await supabase
        .from('elearn_enrollments')
        .select('student_id')
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds)
        .gte('created_at', previousDateFilter.start)
        .lt('created_at', previousDateFilter.end);

      // Avis actuels
      const { data: currentReviews } = await supabase
        .from('elearn_course_reviews')
        .select('rating')
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds)
        .gte('created_at', dateFilter);

      // Avis période précédente
      const { data: previousReviews } = await supabase
        .from('elearn_course_reviews')
        .select('rating')
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds)
        .gte('created_at', previousDateFilter.start)
        .lt('created_at', previousDateFilter.end);

      // Calculs
      // Calculs
      const safeCurrentRevenue = currentRevenue || [];
      const safePreviousRevenue = previousRevenue || [];
      const safeCurrentEnrollments = currentEnrollments || [];
      const safePreviousEnrollments = previousEnrollments || [];
      const safeCurrentReviews = currentReviews || [];
      const safePreviousReviews = previousReviews || [];

      const totalRevenue = safeCurrentRevenue.reduce((sum, item) => sum + item.price_at_purchase, 0);
      const previousTotalRevenue = safePreviousRevenue.reduce((sum, item) => sum + item.price_at_purchase, 0);
      
      const totalStudents = new Set(safeCurrentEnrollments.map(e => e.student_id)).size;
      const previousTotalStudents = new Set(safePreviousEnrollments.map(e => e.student_id)).size;
      
      const totalCourses = courseIds.length;
      
      const averageRating = safeCurrentReviews.length > 0 
        ? safeCurrentReviews.reduce((sum, r) => sum + r.rating, 0) / safeCurrentReviews.length 
        : 0;
      const previousAverageRating = safePreviousReviews.length > 0 
        ? safePreviousReviews.reduce((sum, r) => sum + r.rating, 0) / safePreviousReviews.length 
        : 0;

      // Calculs alignés sur la page Courses
      const potentialTotalRevenue = courses?.reduce((sum, course) => sum + (course.price * course.enrollment_count), 0) || 0;
      const ratedCourses = (courses || []).filter(c => (c.rating_count ?? 0) > 0 && (c.rating_average ?? 0) > 0);
      const averageOfAverageRatings = ratedCourses.length > 0 
        ? ratedCourses.reduce((sum, course) => sum + course.rating_average, 0) / ratedCourses.length 
        : 0;

      return {
        totalRevenue,
        totalStudents,
        totalCourses,
        averageRating: Math.round(averageRating * 10) / 10,
        revenueGrowth: this.calculateGrowth(totalRevenue, previousTotalRevenue),
        studentsGrowth: this.calculateGrowth(totalStudents, previousTotalStudents),
        coursesGrowth: 0, // Les cours ne changent pas rapidement
        ratingGrowth: Math.round((averageRating - previousAverageRating) * 10) / 10,
        // Retourner les nouvelles métriques
        potentialTotalRevenue,
        averageOfAverageRatings: Math.round(averageOfAverageRatings * 10) / 10
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de la vue d\'ensemble:', error);
      throw error;
    }
  }

  /**
   * Récupère les analytics détaillées par cours
   */
  static async getCourseAnalytics(
    tenantId: string,
    instructorId: string,
    timeRange: string = '30d'
  ): Promise<CourseAnalytics[]> {
    try {
      const dateFilter = this.getDateFilter(timeRange);

      const { data: courses } = await supabase
        .from('elearn_courses')
        .select(`
          id,
          title,
          slug,
          price,
          elearn_enrollments(
            id,
            student_id,
            status,
            progress,
            created_at,
            completed_at
          ),
          elearn_course_reviews(
            rating,
            created_at
          ),
          elearn_order_items(
            price,
            elearn_orders!inner(status, created_at)
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      return (courses || []).map(course => {
        const enrollments = course.elearn_enrollments || [];
        const reviews = course.elearn_course_reviews || [];
        const orderItems = course.elearn_order_items || [];

        const recentEnrollments = enrollments.filter(e => new Date(e.created_at) >= new Date(dateFilter));
        const completedEnrollments = enrollments.filter(e => e.status === 'completed');
        
        // elearn_orders est un objet, pas un tableau, car c'est une relation !inner
        const completedOrderItems = orderItems.filter(item => {
          const order = item.elearn_orders as unknown as { status: string; created_at: string } | null;
          return order && order.status === 'completed' && new Date(order.created_at) >= new Date(dateFilter);
        });

        const revenue = completedOrderItems.reduce((sum, item) => sum + item.price, 0);
        const averageRating = reviews.length > 0 
          ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
          : 0;

        const completionRate = enrollments.length > 0 
          ? (completedEnrollments.length / enrollments.length) * 100 
          : 0;

        // Calcul approximatif du temps moyen passé (en minutes)
        const averageTimeSpent = enrollments.length > 0 
          ? enrollments.reduce((sum, e) => sum + (e.progress || 0), 0) * 10 / enrollments.length 
          : 0;

        return {
          id: course.id,
          title: course.title,
          slug: course.slug,
          enrollments: enrollments.length,
          completions: completedEnrollments.length,
          revenue,
          averageRating: Math.round(averageRating * 10) / 10,
          averageTimeSpent: Math.round(averageTimeSpent),
          completionRate: Math.round(completionRate * 10) / 10,
          dropoffRate: Math.round((100 - completionRate) * 10) / 10,
          viewsCount: enrollments.length * 3, // Estimation
          lastWeekEnrollments: recentEnrollments.length,
          conversionRate: 85 // Estimation
        };
      }) || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des analytics de cours:', error);
      throw error;
    }
  }

  /**
   * Récupère les métriques d'engagement des étudiants
   */
  static async getStudentEngagement(
    tenantId: string,
    instructorId: string,
    timeRange: string = '30d'
  ): Promise<StudentEngagement> {
    try {
      const dateFilter = this.getDateFilter(timeRange);

      // Récupération des cours de l'instructeur
      const { data: courses } = await supabase
        .from('elearn_courses')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      const courseIds = courses?.map(c => c.id) || [];

      // Inscriptions récentes
      const { data: enrollments } = await supabase
        .from('elearn_enrollments')
        .select('student_id, status, progress, last_accessed_at, created_at')
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds)
        .gte('created_at', dateFilter);

      const uniqueStudents = new Set(enrollments?.map(e => e.student_id)).size;
      const completedEnrollments = enrollments?.filter(e => e.status === 'completed').length || 0;
      const totalEnrollments = enrollments?.length || 0;

      // Calcul des utilisateurs actifs (approximation)
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const dailyActive = enrollments?.filter(e => e.last_accessed_at && e.last_accessed_at >= oneDayAgo).length || 0;
      const weeklyActive = enrollments?.filter(e => e.last_accessed_at && e.last_accessed_at >= oneWeekAgo).length || 0;
      const monthlyActive = enrollments?.filter(e => e.last_accessed_at && e.last_accessed_at >= oneMonthAgo).length || 0;

      const averageProgress = enrollments && enrollments.length > 0 
        ? enrollments.reduce((sum, e) => sum + (e.progress || 0), 0) / enrollments.length 
        : 0;

      return {
        activeStudents: uniqueStudents,
        averageTimeSpent: Math.round(averageProgress * 10), // Estimation en minutes
        completionRate: totalEnrollments > 0 ? (completedEnrollments / totalEnrollments) * 100 : 0,
        retentionRate: totalEnrollments > 0 ? (monthlyActive / totalEnrollments) * 100 : 0,
        dailyActiveUsers: dailyActive,
        weeklyActiveUsers: weeklyActive,
        monthlyActiveUsers: monthlyActive
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'engagement:', error);
      throw error;
    }
  }

  /**
   * Récupère les analytics de revenus
   */
  static async getRevenueAnalytics(
    tenantId: string,
    instructorId: string,
    timeRange: string = '30d'
  ): Promise<RevenueAnalytics> {
    try {
      // Récupération des cours de l'instructeur
      const { data: courses } = await supabase
        .from('elearn_courses')
        .select('id, title, price, category_id')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      const courseIds = courses?.map(c => c.id) || [];

      // Revenus par mois (6 derniers mois)
      const monthlyRevenue: MonthlyRevenue[] = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).toISOString();
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59).toISOString();

        const { data: monthlyOrders } = await supabase
          .from('elearn_order_items')
          .select(`
            price,
            elearn_orders!inner(created_at, status)
          `)
          .eq('elearn_orders.tenant_id', tenantId)
          .in('course_id', courseIds)
          .eq('elearn_orders.status', 'completed')
          .gte('elearn_orders.created_at', startOfMonth)
          .lte('elearn_orders.created_at', endOfMonth);

        const revenue = monthlyOrders?.reduce((sum, item) => sum + item.price, 0) || 0;
        const enrollments = monthlyOrders?.length || 0;

        monthlyRevenue.push({
          month: date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
          revenue,
          enrollments,
          growth: 0 // Calculé séparément si nécessaire
        });
      }

      // Top des cours vendeurs
      const topSellingCourses: TopCourse[] = [];
      for (const course of courses || []) {
        const { data: courseOrders } = await supabase
          .from('elearn_order_items')
          .select(`
            price,
            elearn_orders!inner(status)
          `)
          .eq('elearn_orders.tenant_id', tenantId)
          .eq('course_id', course.id)
          .eq('elearn_orders.status', 'completed');

        const { data: courseReviews } = await supabase
          .from('elearn_course_reviews')
          .select('rating')
          .eq('tenant_id', tenantId)
          .eq('course_id', course.id);

        const revenue = courseOrders?.reduce((sum, item) => sum + item.price, 0) || 0;
        const enrollments = courseOrders?.length || 0;
        const averageRating = courseReviews && courseReviews.length > 0 
          ? courseReviews.reduce((sum, r) => sum + r.rating, 0) / courseReviews.length 
          : 0;

        topSellingCourses.push({
          id: course.id,
          title: course.title,
          revenue,
          enrollments,
          averageRating: Math.round(averageRating * 10) / 10
        });
      }

      // Tri par revenus
      topSellingCourses.sort((a, b) => b.revenue - a.revenue);

      const totalRevenue = topSellingCourses.reduce((sum, course) => sum + course.revenue, 0);
      const totalTransactions = topSellingCourses.reduce((sum, course) => sum + course.enrollments, 0);

      return {
        monthlyRevenue,
        topSellingCourses: topSellingCourses.slice(0, 10),
        revenueByCategory: [], // À implémenter si nécessaire
        averageOrderValue: totalTransactions > 0 ? totalRevenue / totalTransactions : 0,
        totalTransactions
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des analytics de revenus:', error);
      throw error;
    }
  }

  /**
   * Récupère les analytics des avis et évaluations
   */
  static async getReviewAnalytics(
    tenantId: string,
    instructorId: string,
    limit: number = 10
  ): Promise<ReviewAnalytics> {
    try {
      // Récupération des cours de l'instructeur
      const { data: courses } = await supabase
        .from('elearn_courses')
        .select('id, title')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      const courseIds = courses?.map(c => c.id) || [];

      // Récupération de tous les avis
      const { data: reviews } = await supabase
        .from('elearn_course_reviews')
        .select(`
          id,
          rating,
          review_text,
          created_at,
          student_id,
          course_id
        `)
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds)
        .order('created_at', { ascending: false });

      const totalReviews = reviews?.length || 0;
      const averageRating = totalReviews > 0 && reviews 
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews 
        : 0;

      // Distribution des notes
      const ratingDistribution: RatingDistribution[] = [];
      for (let stars = 1; stars <= 5; stars++) {
        const count = reviews ? reviews.filter(r => r.rating === stars).length : 0;
        const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
        ratingDistribution.push({ stars, count, percentage });
      }

      // Récupérer les informations des étudiants pour afficher le vrai nom
      const studentIds = Array.from(new Set((reviews || []).map(r => r.student_id))).filter(Boolean);
      let usersMap = new Map<string, { full_name: string | null; avatar_url: string | null }>();
      if (studentIds.length > 0) {
        const { data: usersData } = await supabase
          .from('elearn_customer_profiles')
          .select('id, tenant_id, full_name, avatar_url')
          .eq('tenant_id', tenantId)
          .in('id', studentIds as string[]);
        if (usersData) {
          for (const u of usersData) {
            usersMap.set(u.id, { full_name: u.full_name, avatar_url: u.avatar_url });
          }
        }
      }

      // Avis récents avec informations des étudiants (nom réel si disponible)
      const recentReviews: Review[] = (reviews?.slice(0, limit) || []).map(review => {
        const course = courses?.find(c => c.id === review.course_id);
        const userInfo = usersMap.get(review.student_id);
        const displayName = userInfo?.full_name && userInfo.full_name.trim().length > 0
          ? userInfo.full_name
          : `Étudiant ${review.student_id.slice(-4)}`;
        return {
          id: review.id,
          studentName: displayName,
          studentAvatar: userInfo?.avatar_url || null,
          courseName: course?.title || 'Cours inconnu',
          rating: review.rating,
          comment: review.review_text || '',
          date: review.created_at,
          isVerified: true
        };
      });

      // Analyse de sentiment approximative
      const positiveReviews = reviews?.filter(r => r.rating >= 4).length || 0;
      const neutralReviews = reviews?.filter(r => r.rating === 3).length || 0;
      const negativeReviews = reviews?.filter(r => r.rating <= 2).length || 0;

      return {
        totalReviews,
        averageRating: Math.round(averageRating * 10) / 10,
        ratingDistribution,
        recentReviews,
        sentimentAnalysis: {
          positive: totalReviews > 0 ? (positiveReviews / totalReviews) * 100 : 0,
          neutral: totalReviews > 0 ? (neutralReviews / totalReviews) * 100 : 0,
          negative: totalReviews > 0 ? (negativeReviews / totalReviews) * 100 : 0
        }
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des analytics d\'avis:', error);
      throw error;
    }
  }

  /**
   * Récupère les données de série temporelle
   */
  static async getTimeSeriesData(
    tenantId: string,
    instructorId: string,
    timeRange: string = '30d'
  ): Promise<TimeSeriesData[]> {
    try {
      const days = this.getDaysFromTimeRange(timeRange);
      const data: TimeSeriesData[] = [];

      // Récupération des cours de l'instructeur
      const { data: courses } = await supabase
        .from('elearn_courses')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      const courseIds = courses?.map(c => c.id) || [];

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();
        const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).toISOString();

        // Inscriptions du jour
        const { data: dayEnrollments } = await supabase
          .from('elearn_enrollments')
          .select('id, student_id')
          .eq('tenant_id', tenantId)
          .in('course_id', courseIds)
          .gte('created_at', dayStart)
          .lte('created_at', dayEnd);

        // Revenus du jour
        const { data: dayOrders } = await supabase
          .from('elearn_order_items')
          .select(`
            price,
            elearn_orders!inner(created_at, status)
          `)
          .eq('elearn_orders.tenant_id', tenantId)
          .in('course_id', courseIds)
          .eq('elearn_orders.status', 'completed')
          .gte('elearn_orders.created_at', dayStart)
          .lte('elearn_orders.created_at', dayEnd);

        // Complétions du jour
        const { data: dayCompletions } = await supabase
          .from('elearn_enrollments')
          .select('id')
          .eq('tenant_id', tenantId)
          .in('course_id', courseIds)
          .eq('status', 'completed')
          .gte('completed_at', dayStart)
          .lte('completed_at', dayEnd);

        data.push({
          date: date.toISOString().split('T')[0],
          enrollments: dayEnrollments?.length || 0,
          revenue: dayOrders?.reduce((sum, item) => sum + item.price, 0) || 0,
          completions: dayCompletions?.length || 0,
          activeStudents: new Set(dayEnrollments?.map(e => e.student_id)).size
        });
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des données temporelles:', error);
      throw error;
    }
  }

  /**
   * Récupère la liste détaillée des étudiants d'un instructeur
   */
  static async getStudentDetails(
    tenantId: string,
    instructorId: string
  ): Promise<StudentDetails[]> {
    try {
      // 1. Récupérer les cours de l'instructeur
      const { data: courses, error: coursesError } = await supabase
        .from('elearn_courses')
        .select('id, title')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      if (coursesError) throw coursesError;
      const courseIds = courses.map(c => c.id);
      if (courseIds.length === 0) return [];

      // 2. Récupérer toutes les inscriptions pour ces cours (sans jointure implicite)
      const { data: enrollments, error: enrollmentsError } = await supabase
        .from('elearn_enrollments')
        .select(`
          id,
          student_id,
          course_id,
          status,
          progress_percentage,
          completed_at,
          enrolled_at,
          created_at,
          updated_at,
          last_accessed_at
        `)
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds);

      if (enrollmentsError) throw enrollmentsError;

      // 3. Récupérer l'activité de progression des leçons pour affiner 'last_activity'
      const enrollmentIds = Array.from(new Set((enrollments || []).map(e => e.id))).filter(Boolean);
      let progressActivityByEnrollment = new Map<string, string>();
      if (enrollmentIds.length > 0) {
        const { data: progressRows, error: progressErr } = await supabase
          .from('elearn_lesson_progress')
          .select('enrollment_id, created_at, updated_at, completed_at')
          .in('enrollment_id', enrollmentIds);
        if (progressErr) throw progressErr;
        for (const row of (progressRows || [])) {
          const times = [row.updated_at, row.completed_at, row.created_at].filter((d: string | null): d is string => Boolean(d));
          if (times.length === 0) continue;
          const maxIso = new Date(Math.max(...times.map(d => new Date(d).getTime()))).toISOString();
          const prev = progressActivityByEnrollment.get(row.enrollment_id);
          if (!prev || new Date(maxIso) > new Date(prev)) {
            progressActivityByEnrollment.set(row.enrollment_id, maxIso);
          }
        }
      }

      // 4. Récupérer les infos des utilisateurs séparément et agréger par étudiant
      const studentsMap = new Map<string, any>();

      const studentIds = Array.from(new Set((enrollments || []).map(e => e.student_id))).filter(Boolean);

      let usersMap = new Map<string, { id: string; email: string; full_name: string | null; avatar_url: string | null }>();
      if (studentIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('elearn_customer_profiles')
          .select('id, email, full_name, avatar_url')
          .in('id', studentIds);
        if (usersError) throw usersError;
        usersMap = new Map(usersData.map(u => [u.id, u]));
      }

      // Normaliser les inscriptions pour fournir un champ 'progress' basé sur 'progress_percentage'
      const normalizedEnrollments = (enrollments || []).map(e => ({ ...e, progress: (e as any).progress_percentage ?? 0 }));

      for (const enrollment of normalizedEnrollments) {
        const user = usersMap.get(enrollment.student_id);
        if (!user) continue;

        const studentId = enrollment.student_id;
        if (!studentsMap.has(studentId)) {
          const derivedName = user.full_name || (user.email ? (user.email.split('@')[0] || user.email) : `Etudiant-${studentId}`);
          studentsMap.set(studentId, {
            id: studentId,
            name: derivedName,
            email: user.email,
            avatar_url: user.avatar_url,
            enrollment_date: new Date().toISOString(), // Placeholder, on prendra la plus ancienne
            last_activity: new Date(0).toISOString(), // Placeholder, on prendra la plus récente
            total_courses: 0,
            completed_courses: 0,
            total_progress: 0,
            total_time_spent: 0, // TODO
            average_grade: 0, // TODO
            courses: [],
          });
        }

        const studentData = studentsMap.get(studentId);
        const courseInfo = courses.find(c => c.id === enrollment.course_id);

        studentData.courses.push({
          course_id: enrollment.course_id,
          course_title: courseInfo?.title || 'Titre inconnu',
          enrollment_date: enrollment.enrolled_at || enrollment.created_at,
          progress: enrollment.progress,
          last_accessed: enrollment.last_accessed_at,
          completion_date: enrollment.completed_at,
          grade: null, // TODO
          time_spent: 0, // TODO
        });

        studentData.total_courses += 1;
        if (enrollment.status === 'completed') {
          studentData.completed_courses += 1;
        }
        studentData.total_progress += enrollment.progress;

        const enrollmentDateCandidate = enrollment.enrolled_at || enrollment.created_at;
        if (enrollmentDateCandidate && new Date(enrollmentDateCandidate) < new Date(studentData.enrollment_date)) {
          studentData.enrollment_date = enrollmentDateCandidate;
        }
        // Déterminer une activité candidate robuste: max(last_accessed_at, completed_at, updated_at, enrolled_at, created_at, progression_leçon)
        const candidateDates = [
          enrollment.last_accessed_at,
          enrollment.completed_at,
          enrollment.updated_at,
          enrollment.enrolled_at,
          enrollment.created_at,
          progressActivityByEnrollment.get(enrollment.id),
        ]
          .filter((d): d is string => Boolean(d));
        if (candidateDates.length > 0) {
          const candidateActivityIso = new Date(
            Math.max(...candidateDates.map(d => new Date(d).getTime()))
          ).toISOString();
          if (new Date(candidateActivityIso) > new Date(studentData.last_activity)) {
            studentData.last_activity = candidateActivityIso;
          }
        }
      }

      // 4. Finaliser les calculs et formater la sortie
      const studentDetails: StudentDetails[] = Array.from(studentsMap.values()).map(student => {
        const avgProgress = student.total_courses > 0 ? student.total_progress / student.total_courses : 0;
        
        let status: StudentDetails['status'] = 'inactive';
        const lastActivityDate = new Date(student.last_activity);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        if (lastActivityDate > thirtyDaysAgo) {
          status = 'active';
        }
        if (student.completed_courses === student.total_courses && student.total_courses > 0) {
          status = 'completed';
        }

        return {
          ...student,
          total_progress: Math.round(avgProgress),
          status,
        };
      });

      return studentDetails;

    } catch (error) {
      console.error('Erreur lors de la récupération des détails des étudiants:', error);
      throw error;
    }
  }

  // Méthodes utilitaires privées

  private static getDateFilter(timeRange: string): string {
    const now = new Date();
    switch (timeRange) {
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      case '90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      case '1y':
        return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
      default:
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    }
  }

  private static getPreviousDateFilter(timeRange: string): { start: string; end: string } {
    const now = new Date();
    const days = this.getDaysFromTimeRange(timeRange);
    
    const currentStart = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    const previousStart = new Date(currentStart.getTime() - days * 24 * 60 * 60 * 1000);
    
    return {
      start: previousStart.toISOString(),
      end: currentStart.toISOString()
    };
  }

  private static getDaysFromTimeRange(timeRange: string): number {
    switch (timeRange) {
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      case '1y': return 365;
      default: return 30;
    }
  }

  private static calculateGrowth(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100 * 10) / 10;
  }
}

export default InstructorAnalyticsService;
